import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import bgImage from '../../assets/images/background/bg-7.jpg';

const ProgramSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(false);
  
  // Parse query parameters from URL
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('session_id');

  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');

  // Button styling
  const buttonStyle = {
    padding: '15px 30px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    textDecoration: 'none',
    textTransform: 'uppercase' as const,
    letterSpacing: '3px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '0',
    transition: 'all 0.3s ease',
    margin: '5px',
    display: 'inline-block',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
  };

  // Handle manual program check
  const handleManualProgramCheck = async () => {
    if (!sessionId || !isLoggedIn) return;
    
    try {
      setIsChecking(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://what-workout25.vercel.app'}/api/payment/check-program/${sessionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          alert('Program created successfully! Go to your profile to view it.');
          navigate('/profile');
        } else {
          alert('Could not find your program. Please contact support.');
        }
      } else {
        alert('Error checking program. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error checking program:', error);
      alert('Error checking for your program. Please try again later.');
    } finally {
      setIsChecking(false);
    }
  };

  // Automatically redirect to profile/login if not logged in
  useEffect(() => {
    if (!isLoggedIn && sessionId) {
      // Save the session ID to localStorage so it can be accessed after login
      localStorage.setItem('pendingProgramSessionId', sessionId);
      
      // Redirect to profile page after a short delay
      const timer = setTimeout(() => {
        navigate('/profile', { state: { fromPayment: true, message: 'Please log in to view your purchased program.' } });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, sessionId, navigate]);

  return (
    <div style={{
      backgroundImage: `url(${bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      minHeight: '100vh',
      padding: '40px 20px',
      position: 'relative',
      color: 'white',
      fontFamily: "'Montserrat', sans-serif",
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <div style={{
        maxWidth: '800px',
        padding: '40px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '8px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontSize: '36px',
          marginBottom: '30px',
          fontWeight: '600',
          letterSpacing: '2px',
        }}>
          PAYMENT SUCCESSFUL!
        </h1>
        
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            margin: '20px auto',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(50, 205, 50, 0.2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <div style={{
              fontSize: '40px',
              color: '#4CAF50',
            }}>✓</div>
          </div>
          
          <p style={{ fontSize: '18px', marginBottom: '15px' }}>
            Your payment has been processed successfully!
          </p>
          
          {!isLoggedIn && (
            <p style={{ fontSize: '16px', color: '#ffcc00' }}>
              You will be redirected to the login page in a few seconds to access your program.
            </p>
          )}
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/profile')}
            style={{
              ...buttonStyle,
              backgroundColor: 'rgba(50, 205, 50, 0.15)',
              borderColor: 'rgba(50, 205, 50, 0.3)',
            }}
          >
            {isLoggedIn ? 'VIEW YOUR PROGRAM' : 'LOG IN TO VIEW PROGRAM'}
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              ...buttonStyle,
              backgroundColor: 'rgba(70, 130, 180, 0.3)',
              borderColor: 'rgba(70, 130, 180, 0.5)',
            }}
          >
            RETURN TO HOME
          </button>
          {isLoggedIn && (
            <button
              onClick={handleManualProgramCheck}
              disabled={isChecking}
              style={{
                ...buttonStyle,
                backgroundColor: 'rgba(255, 193, 7, 0.3)',
                borderColor: 'rgba(255, 193, 7, 0.5)',
                marginTop: '15px',
                opacity: isChecking ? 0.7 : 1,
              }}
            >
              {isChecking ? 'CHECKING...' : 'CHECK/CREATE PROGRAM'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramSuccessPage;